/** Réglages, difficulté, questionnaire de niveau et bandeau de retour d'un utilisateur. */
import type { DifficultySettings, LearningMode, OnboardingAnswers, UserSettings } from '@unity-learning/shared';
import { apiGet, apiPost, apiPut } from './client';

export function getUserSettings(userId: string): Promise<UserSettings> {
  return apiGet<UserSettings>(`/api/users/${encodeURIComponent(userId)}/settings`);
}

/**
 * Met à jour une partie des réglages. `keepalive` laisse partir la requête même si
 * la page est rechargée aussitôt après (changement de mode suivi d'un F5).
 */
export function updateUserSettings(
  userId: string,
  settings: Partial<UserSettings>,
  options: { keepalive?: boolean } = {}
): Promise<UserSettings> {
  return apiPut<UserSettings>(`/api/users/${encodeURIComponent(userId)}/settings`, settings, options);
}

export interface NeglectedKp {
  id: string;
  code: string;
  name: string;
  nodeId: string;
  lastAttemptAt: string;
}

export interface ComebackData {
  lastActivityAt: string | null;
  hoursSinceLastActivity: number | null;
  lastExercise: { title: string; isCorrect: boolean; submittedAt: string } | null;
  neglectedKps: NeglectedKp[];
  kpTotals: { total: number; completed: number };
}

export function getComeback(userId: string, learningMode: LearningMode): Promise<ComebackData> {
  return apiGet<ComebackData>(`/api/users/${encodeURIComponent(userId)}/comeback`, {
    query: { learning_mode: learningMode },
  });
}

export function getDifficultySettings(userId: string): Promise<DifficultySettings> {
  return apiGet<DifficultySettings>(`/api/difficulty/${encodeURIComponent(userId)}`);
}

export function updateDifficultySettings(
  userId: string,
  settings: Partial<DifficultySettings> & { learningMode: LearningMode }
): Promise<DifficultySettings> {
  return apiPut<DifficultySettings>(`/api/difficulty/${encodeURIComponent(userId)}`, settings);
}

export interface OnboardingResult {
  settings: Omit<DifficultySettings, 'updatedAt'>;
  onboardingCompletedAt: string;
  skipped: boolean;
}

/**
 * Questionnaire de niveau du premier login : `answers` règle les axes de difficulté côté
 * serveur (`computeOnboardingSettings` de shared), `skipped: true` pose seulement la date.
 * Après l'appel, `refreshUser()` pour que `user.onboardingCompletedAt` soit à jour.
 */
export function completeOnboarding(
  userId: string,
  body: { learningMode: LearningMode; answers?: OnboardingAnswers; skipped?: boolean }
): Promise<OnboardingResult> {
  return apiPost<OnboardingResult>(`/api/users/${encodeURIComponent(userId)}/onboarding`, body);
}
